import { isString } from 'lodash';
import { useMemo } from 'react';

import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AfterDeath } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { HypotheticalTransferUtils_EstateWaterfallHypotheticalTransferFragment } from '@/utils/hypotheticalTransfers/graphql/HypotheticalTransferUtils.generated';

import { useEstateWaterfallContext } from '../../contexts/estateWaterfall.context';
import { EstateWaterfall_EdgeFragment } from '../../graphql/EstateWaterfall.generated';
import {
  EstateWaterfallGraphNodeAttributes,
  GraphNode,
  GraphNodeData,
} from '../../types';
import { EXTERNAL_TRANSFER_NODE_IDENTIFIER } from '../../waterfallGraph/constants';
import { createEdgeCards } from './Cards/createEdgeCards';
import { createExternalTransferCards } from './Cards/createExternalTransferCards';
import { EstateWaterfallEdgeLabelProps } from './types';

export function useEdgeLabelCards({
  edge,
  source,
  target,
  label,
  value,
}: EstateWaterfallEdgeLabelProps) {
  const { state, dispatch } = useEstateWaterfallContext();
  // Get source and target information
  const sourceNode = state.derivedData.nodesById[source];
  const targetNode = state.derivedData.nodesById[target];

  const waterfallTransfers = state.derivedData.transfers;

  const hypotheticalTransfers: HypotheticalTransferUtils_EstateWaterfallHypotheticalTransferFragment[] =
    useMemo(() => {
      return getNodes(state.waterfall.hypotheticalTransfers);
    }, [state.waterfall.hypotheticalTransfers]);

  let sourceNodeAttributes: EstateWaterfallGraphNodeAttributes<
    GraphNode,
    GraphNodeData
  > | null = null;
  let targetNodeAttributes: EstateWaterfallGraphNodeAttributes<
    GraphNode,
    GraphNodeData
  > | null = null;

  try {
    sourceNodeAttributes = sourceNode
      ? (state.waterfallGraph.getNodeAttributes(
          source
        ) as EstateWaterfallGraphNodeAttributes<GraphNode, GraphNodeData>)
      : null;
    targetNodeAttributes = targetNode
      ? (state.waterfallGraph.getNodeAttributes(
          target
        ) as EstateWaterfallGraphNodeAttributes<GraphNode, GraphNodeData>)
      : null;
  } catch {
    // getNodeAttributes throws an error if the node is not found
  }

  // Get display name from node data
  const sourceEntityName =
    sourceNode && isString(sourceNode.data?.lineOne)
      ? sourceNode.data.lineOne
      : '';
  const targetEntityName =
    targetNode && isString(targetNode.data?.lineOne)
      ? targetNode.data.lineOne
      : '';

  const sourceNodeDeathSection = sourceNodeAttributes?.data.afterDeath;

  const sourceIsTestamentaryEntity = (() => {
    const nodeData = sourceNodeAttributes?.data;
    if (nodeData && 'node' in nodeData) {
      const entity = nodeData.node;
      return entity.__typename === 'TestamentaryEntity';
    }
    return false;
  })();

  const sourceIsClientProfile = (() => {
    const nodeData = sourceNodeAttributes?.data;
    if (nodeData && 'node' in nodeData) {
      const node = nodeData.node;
      return node.__typename === 'ClientProfile';
    }
    return false;
  })();

  const entityType: EntityType | null = useMemo(() => {
    const nodeData = sourceNodeAttributes?.data;
    if (nodeData && 'node' in nodeData) {
      const entity = nodeData.node;

      const isEntity = entity.__typename === 'Entity';
      if (isEntity) {
        return getEntityTypeFromEntityKind(entity.entityKind);
      }
    }
    return null;
  }, [sourceNodeAttributes]);

  const deathYear = state.waterfall.firstGrantorDeathYear || undefined;

  const firstDyingGrantor = state.primaryClients.find(
    (client) => client.id === state.waterfall.firstGrantorDeath?.id
  );

  const secondDyingGrantor = state.primaryClients.find(
    (client) => client.id !== firstDyingGrantor?.id
  );

  const decedentName =
    sourceNodeDeathSection === AfterDeath.None
      ? firstDyingGrantor?.displayGivenName
      : secondDyingGrantor?.displayGivenName;

  const waterfallEdgeFragments: EstateWaterfall_EdgeFragment[] =
    state.waterfall.visualizationWithProjections.edges;

  return useMemo(() => {
    if (
      source.includes(EXTERNAL_TRANSFER_NODE_IDENTIFIER) ||
      target.includes(EXTERNAL_TRANSFER_NODE_IDENTIFIER)
    ) {
      const externalTransferCards = createExternalTransferCards({
        source,
        target,
        sourceEntityName,
        targetEntityName,
        hypotheticalTransfers,
      });

      return {
        cards: externalTransferCards,
      };
    }

    if (!sourceNodeAttributes || !targetNodeAttributes) {
      return { cards: [] };
    }

    const cards = createEdgeCards({
      edge,
      source,
      target,
      sourceEntityName,
      targetEntityName,
      sourceIsTestamentaryEntity,
      sourceIsClientProfile,
      entityType,
      label,
      value,
      decedentName,
      deathYear,
      sourceNodeAttributes,
      targetNodeAttributes,
      dispatch,
      waterfallTransfers,
      waterfallEdgeFragments,
      firstGrantorDeathYear: state.waterfall.firstGrantorDeathYear,
      secondGrantorDeathYear: state.waterfall.secondGrantorDeathYear,
      firstGrantorId: firstDyingGrantor?.id,
      secondGrantorId: secondDyingGrantor?.id,
    });

    return { cards };
  }, [
    sourceNodeAttributes,
    targetNodeAttributes,
    edge,
    source,
    target,
    sourceEntityName,
    targetEntityName,
    sourceIsTestamentaryEntity,
    sourceIsClientProfile,
    entityType,
    label,
    value,
    decedentName,
    deathYear,
    dispatch,
    waterfallTransfers,
    waterfallEdgeFragments,
    hypotheticalTransfers,
    state.waterfall.firstGrantorDeathYear,
    state.waterfall.secondGrantorDeathYear,
    firstDyingGrantor?.id,
    secondDyingGrantor?.id,
  ]);
}
